import { useCallback } from 'react';
import METRIC_BUDGETS from '../config/metricBudgets';
import { evaluatePoint, evaluatorScoreToFps, evaluateBadness } from '../lib/evaluator';

/**
 * Hook providing metric-to-FPS conversion, metric-to-badness conversion,
 * and metric configuration for heatmap color mapping and legend display.
 *
 * @param {string} colorMetric - Selected metric key. Builtin: 'framerate', 'gpuFps', etc.
 *                                Evaluator: 'eval:<id>' where id is a Firestore evaluator doc ID.
 * @param {object|null} activeEvaluator - If colorMetric is 'eval:*', the full evaluator object
 *                                         { mode, rules: [{ metric, threshold }], heatmapLow, heatmapHigh }
 * @returns {{ getMetricFps: Function, getMetricBadness: Function, getMetricConfig: Function }}
 */
// Config entries for budget-based metrics that share the same pattern
const BUDGET_METRIC_LABELS = {
  setPassCalls:              { label: 'SetPass Calls',              unit: 'calls' },
  shadowCasterRenderers:     { label: 'Shadow Casters',             unit: 'renderers' },
  candidateLights:           { label: 'Lights in View',             unit: 'lights' },
  candidateShadowCastingLights: { label: 'Shadow-Casting Lights',  unit: 'lights' },
  transparentDraws:          { label: 'Transparent Draws',          unit: 'draws' },
  opaqueDraws:               { label: 'Opaque Draws',               unit: 'draws' },
  depthPrepassDraws:         { label: 'Depth Prepass Draws',        unit: 'draws' },
  mainLightShadowDraws:      { label: 'Main Light Shadow Draws',    unit: 'draws' },
  additionalLightShadowDraws: { label: 'Addl Light Shadow Draws',  unit: 'draws' },
  batchedDraws:              { label: 'Batched Draws',              unit: 'draws' },
  unbatchedDraws:            { label: 'Unbatched Draws',            unit: 'draws' },
  distinctShaderVariants:    { label: 'Shader Variants',            unit: 'variants' },
  visibleRenderers:          { label: 'Visible Renderers',          unit: 'renderers' },
  candidateRenderers:        { label: 'Renderer Density',           unit: 'renderers' },
  candidateTriangles:        { label: 'Triangle Density',           unit: 'tris' },
};

const budgetMetricConfigs = Object.fromEntries(
  Object.entries(BUDGET_METRIC_LABELS)
    .filter(([key]) => METRIC_BUDGETS[key] != null)
    .map(([key, { label, unit }]) => {
      const b = METRIC_BUDGETS[key];
      return [key, {
        label,
        unit,
        ticks: [0, Math.round(b * 0.5), b, b * 2],
        lowerIsBetter: true,
        getValue: (p) => p[key],
        format: (v) => v != null ? (v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? Math.round(v / 1000) + 'k' : Math.round(v)) : null
      }];
    })
);

function useMetricConfig(colorMetric, activeEvaluator = null) {
  const isEvaluator = colorMetric?.startsWith('eval:');

  const getMetricFps = useCallback((point) => {
    if (isEvaluator && activeEvaluator) {
      const { score } = evaluatePoint(point, activeEvaluator);
      return evaluatorScoreToFps(score);
    }
    if (colorMetric === 'framerate') {
      return point.framerate || null;
    } else if (colorMetric === 'gpuFps') {
      return point.gpuTime ? 1000 / point.gpuTime : null;
    } else if (colorMetric === 'cpuFps') {
      return point.cpuTime ? 1000 / point.cpuTime : null;
    } else if (colorMetric === 'drawCalls') {
      if (point.drawCalls == null) return null;
      const b = METRIC_BUDGETS.drawCalls;
      return Math.max(20, Math.min(45, 45 - (point.drawCalls / b) * 15));
    } else if (colorMetric === 'batches') {
      if (point.batches == null) return null;
      const b = METRIC_BUDGETS.batches;
      return Math.max(20, Math.min(45, 45 - (point.batches / b) * 15));
    } else if (colorMetric === 'triangles') {
      if (point.triangles == null) return null;
      const b = METRIC_BUDGETS.triangles;
      return Math.max(20, Math.min(45, 45 - (point.triangles / b) * 15));
    } else if (colorMetric === 'vertices') {
      if (point.vertices == null) return null;
      const b = METRIC_BUDGETS.vertices;
      return Math.max(20, Math.min(45, 45 - (point.vertices / b) * 15));
    } else if (colorMetric === 'cullingRatio') {
      if (point.candidateRenderers == null || point.candidateRenderers === 0) return null;
      const ratio = (point.visibleRenderers ?? 0) / point.candidateRenderers;
      // ratio 0.0 (100% culled) → 45 FPS (green), ratio 1.0 (nothing culled) → 20 FPS (red)
      return Math.max(20, Math.min(45, 45 - ratio * 25));
    } else if (METRIC_BUDGETS[colorMetric] != null) {
      // Generic budget-based metric (setPassCalls, transparentDraws, etc.)
      const val = point[colorMetric];
      if (val == null) return null;
      const b = METRIC_BUDGETS[colorMetric];
      return Math.max(20, Math.min(45, 45 - (val / b) * 15));
    }
    return point.framerate || null;
  }, [colorMetric, isEvaluator, activeEvaluator]);

  // Direct badness score (0 = good/green, 1 = bad/red) for heatmap accumulation.
  // Uses per-rule low/high thresholds for evaluators.
  const getMetricBadness = useCallback((point) => {
    if (isEvaluator && activeEvaluator) {
      return evaluateBadness(point, activeEvaluator);
    }
    // Built-in metrics: convert to badness via FPS equivalent
    const fps = getMetricFps(point);
    if (fps == null) return 0.5;
    // Default range: 45fps = good (0), 20fps = bad (1)
    if (fps >= 45) return 0.0;
    if (fps <= 20) return 1.0;
    return 1.0 - (fps - 20) / 25;
  }, [colorMetric, isEvaluator, activeEvaluator, getMetricFps]);

  const getMetricConfig = useCallback((point) => {
    if (isEvaluator && activeEvaluator) {
      const config = {
        label: activeEvaluator.name || 'Evaluator',
        unit: '% of budget',
        ticks: ['0%', '50%', '100%', '150%'],
        lowerIsBetter: true,
        getValue: (p) => evaluatePoint(p, activeEvaluator).score,
        format: (v) => v != null ? Math.round(v * 100) + '%' : null
      };
      if (point) {
        return {
          ...config,
          rawValue: config.getValue(point),
          displayValue: config.format(config.getValue(point))
        };
      }
      return config;
    }

    const configs = {
      framerate: {
        label: 'Framerate',
        unit: 'FPS',
        ticks: [20, 25, 30, 45],
        lowerIsBetter: false,
        getValue: (p) => p.framerate,
        format: (v) => v?.toFixed(1)
      },
      gpuFps: {
        label: 'GPU',
        unit: 'FPS',
        ticks: [20, 25, 30, 45],
        lowerIsBetter: false,
        getValue: (p) => p.gpuTime ? 1000 / p.gpuTime : null,
        format: (v) => v?.toFixed(1)
      },
      cpuFps: {
        label: 'CPU',
        unit: 'FPS',
        ticks: [20, 25, 30, 45],
        lowerIsBetter: false,
        getValue: (p) => p.cpuTime ? 1000 / p.cpuTime : null,
        format: (v) => v?.toFixed(1)
      },
      drawCalls: {
        label: 'Draw Calls',
        unit: 'draws',
        ticks: [0, Math.round(METRIC_BUDGETS.drawCalls * 0.5), METRIC_BUDGETS.drawCalls, METRIC_BUDGETS.drawCalls * 2],
        lowerIsBetter: true,
        getValue: (p) => p.drawCalls,
        format: (v) => v != null ? Math.round(v).toLocaleString() : null
      },
      batches: {
        label: 'Batches',
        unit: 'batches',
        ticks: [0, Math.round(METRIC_BUDGETS.batches * 0.5), METRIC_BUDGETS.batches, METRIC_BUDGETS.batches * 2],
        lowerIsBetter: true,
        getValue: (p) => p.batches,
        format: (v) => v != null ? Math.round(v).toLocaleString() : null
      },
      triangles: {
        label: 'Triangles',
        unit: 'tris',
        ticks: [0, Math.round(METRIC_BUDGETS.triangles * 0.5), METRIC_BUDGETS.triangles, METRIC_BUDGETS.triangles * 2],
        lowerIsBetter: true,
        getValue: (p) => p.triangles,
        format: (v) => v != null ? (v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? Math.round(v / 1000) + 'k' : Math.round(v)) : null
      },
      vertices: {
        label: 'Vertices',
        unit: 'verts',
        ticks: [0, Math.round(METRIC_BUDGETS.vertices * 0.5), METRIC_BUDGETS.vertices, METRIC_BUDGETS.vertices * 2],
        lowerIsBetter: true,
        getValue: (p) => p.vertices,
        format: (v) => v != null ? (v >= 1000 ? Math.round(v / 1000) + 'k' : Math.round(v)) : null
      },
      cullingRatio: {
        label: 'Culling Ratio',
        unit: '%',
        ticks: ['0%', '25%', '50%', '75%', '100%'],
        lowerIsBetter: true,
        getValue: (p) => p.candidateRenderers > 0 ? (p.visibleRenderers ?? 0) / p.candidateRenderers : null,
        format: (v) => v != null ? Math.round(v * 100) + '%' : null
      },
      ...budgetMetricConfigs
    };
    const config = configs[colorMetric] || configs.framerate;
    if (point) {
      return {
        ...config,
        rawValue: config.getValue(point),
        displayValue: config.format(config.getValue(point))
      };
    }
    return config;
  }, [colorMetric, isEvaluator, activeEvaluator]);

  return { getMetricFps, getMetricBadness, getMetricConfig };
}

export default useMetricConfig;
